import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import {
  Globe, Key, CheckCircle2, XCircle, AlertCircle, Loader2,
  RefreshCw, Plus, Trash2, Play, Eye, EyeOff, Download, Save,
  Zap, Search
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const authHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
});

const SUB_BADGE = {
  active:   { label: 'Activa',      className: 'bg-green-100 text-green-700 border-green-200' },
  inactive: { label: 'Sin suscr.',  className: 'bg-red-100 text-red-700 border-red-200' },
  unknown:  { label: 'Sin verificar', className: 'bg-slate-100 text-slate-500 border-slate-200' },
};

function SubscriptionBadge({ status }) {
  const cfg = SUB_BADGE[status] || SUB_BADGE.unknown;
  return (
    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${cfg.className}`}>
      {cfg.label}
    </span>
  );
}

export default function AdminRapidAPI() {
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [refreshing, setRefreshing]   = useState(false);
  const [status, setStatus]           = useState(null);

  const [apiKey, setApiKey]           = useState('');
  const [showKey, setShowKey]         = useState(false);

  const [filter, setFilter]           = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  const [showImport, setShowImport]   = useState(false);
  const [importHosts, setImportHosts] = useState('');
  const [importing, setImporting]     = useState(false);

  const [showAdd, setShowAdd]         = useState(false);
  const [addForm, setAddForm]         = useState({
    name: '', host: '', category: 'general', description: '',
    icon: '⚡', plan: 'freemium', calls_month: 'Según suscripción',
    tags: '', endpoint_url: '', endpoint_method: 'GET', query_param: 'query'
  });
  const [adding, setAdding]           = useState(false);

  const [testingId, setTestingId]     = useState(null);
  const [testQuery, setTestQuery]     = useState('');
  const [testResults, setTestResults] = useState({});

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/rapidapi/status`, { headers: authHeaders() });
      if (res.ok) setStatus(await res.json());
    } catch { toast.error('Error al cargar el estado de RapidAPI'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const saveKey = async () => {
    if (!apiKey.trim()) { toast.error('Ingresa una API key válida'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/rapidapi/configure`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ api_key: apiKey.trim() })
      });
      if (res.ok) {
        toast.success('API key guardada correctamente');
        setApiKey('');
        loadStatus();
      } else {
        const d = await res.json();
        toast.error(d.detail || 'Error al guardar');
      }
    } catch { toast.error('Error de conexión'); }
    finally { setSaving(false); }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/rapidapi/refresh`, {
        method: 'POST', headers: authHeaders()
      });
      const d = await res.json();
      if (res.ok) {
        toast.success(`Verificación completa: ${d.verified_active} API${d.verified_active !== 1 ? 's' : ''} activa${d.verified_active !== 1 ? 's' : ''}`);
        loadStatus();
      } else { toast.error(d.detail || 'Error al verificar'); }
    } catch { toast.error('Error de conexión'); }
    finally { setRefreshing(false); }
  };

  const handleToggle = async (apiId, currentEnabled) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/rapidapi/${apiId}/toggle`, {
        method: 'PUT', headers: authHeaders()
      });
      if (res.ok) {
        const d = await res.json();
        toast.success(d.enabled ? 'API activada' : 'API desactivada');
        setStatus(prev => prev ? {
          ...prev,
          apis: prev.apis.map(a => a.id === apiId ? { ...a, enabled: d.enabled } : a)
        } : prev);
      }
    } catch { toast.error('Error al cambiar estado'); }
  };

  const handleDelete = async (apiId, apiName) => {
    if (!window.confirm(`¿Eliminar "${apiName}" del catálogo?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/rapidapi/catalog/${apiId}`, {
        method: 'DELETE', headers: authHeaders()
      });
      if (res.ok) {
        toast.success(`"${apiName}" eliminada`);
        setStatus(prev => prev ? { ...prev, apis: prev.apis.filter(a => a.id !== apiId) } : prev);
      } else {
        const d = await res.json();
        toast.error(d.detail || 'Error al eliminar');
      }
    } catch { toast.error('Error de conexión'); }
  };

  const handleImport = async () => {
    const hosts = importHosts.split('\n').map(h => h.trim()).filter(Boolean);
    if (!hosts.length) { toast.error('Ingresa al menos un host'); return; }
    setImporting(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/rapidapi/import-hosts`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ hosts })
      });
      const d = await res.json();
      if (res.ok) {
        toast.success(`${d.imported} importada${d.imported !== 1 ? 's' : ''}, ${d.skipped} omitida${d.skipped !== 1 ? 's' : ''}`);
        setShowImport(false);
        setImportHosts('');
        loadStatus();
      } else { toast.error(d.detail || 'Error al importar'); }
    } catch { toast.error('Error de conexión'); }
    finally { setImporting(false); }
  };

  const handleAdd = async () => {
    if (!addForm.name.trim() || !addForm.host.trim()) {
      toast.error('Nombre y host son obligatorios'); return;
    }
    setAdding(true);
    try {
      const payload = {
        ...addForm,
        tags: addForm.tags ? addForm.tags.split(',').map(t => t.trim()).filter(Boolean) : []
      };
      const res = await fetch(`${API_URL}/api/admin/rapidapi/catalog/add`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify(payload)
      });
      const d = await res.json();
      if (res.ok) {
        toast.success(d.message || 'API añadida al catálogo');
        setShowAdd(false);
        setAddForm({ name: '', host: '', category: 'general', description: '', icon: '⚡', plan: 'freemium', calls_month: 'Según suscripción', tags: '', endpoint_url: '', endpoint_method: 'GET', query_param: 'query' });
        loadStatus();
      } else { toast.error(d.detail || 'Error al añadir'); }
    } catch { toast.error('Error de conexión'); }
    finally { setAdding(false); }
  };

  const handleTest = async (apiId) => {
    if (!testQuery.trim()) { toast.error('Escribe una consulta de prueba'); return; }
    setTestingId(apiId);
    try {
      const res = await fetch(
        `${API_URL}/api/admin/rapidapi/search/${apiId}?query=${encodeURIComponent(testQuery)}&limit=5`,
        { headers: authHeaders() }
      );
      const d = await res.json();
      if (res.ok) {
        setTestResults(prev => ({ ...prev, [apiId]: { ok: true, data: d } }));
      } else {
        setTestResults(prev => ({ ...prev, [apiId]: { ok: false, error: d.detail || 'Error' } }));
        toast.error(d.detail || 'Error al consultar la API');
      }
    } catch { toast.error('Error de conexión'); }
    finally { setTestingId(null); }
  };

  const allCategories = status?.apis
    ? ['all', ...new Set(status.apis.map(a => a.category).filter(Boolean))]
    : ['all'];

  const filteredApis = status?.apis?.filter(api => {
    const q = filter.toLowerCase();
    const matchText = !q || api.name.toLowerCase().includes(q) || api.host.toLowerCase().includes(q) || api.description?.toLowerCase().includes(q);
    const matchCat  = categoryFilter === 'all' || api.category === categoryFilter;
    return matchText && matchCat;
  }) ?? [];

  if (loading) return (
    <div className="flex items-center justify-center h-96">
      <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
    </div>
  );

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
            <Globe className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold">RapidAPI Hub</h1>
            <p className="text-sm text-muted-foreground">Catálogo dinámico de APIs para agentes IA</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing || !status?.key_configured}>
            {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            <span className="ml-2 hidden sm:inline">Verificar suscripciones</span>
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowImport(true)}>
            <Download className="h-4 w-4 mr-2" />
            Importar hosts
          </Button>
          <Button size="sm" onClick={() => setShowAdd(true)}
            className="bg-orange-500 hover:bg-orange-600 text-white">
            <Plus className="h-4 w-4 mr-2" />
            Añadir API
          </Button>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Estado', value: status?.key_configured ? 'Conectado' : 'Sin configurar',
            icon: status?.key_configured ? CheckCircle2 : XCircle,
            color: status?.key_configured ? 'text-green-600' : 'text-red-500' },
          { label: 'APIs totales', value: status?.total_apis ?? 0, icon: Globe, color: 'text-indigo-500' },
          { label: 'APIs activas', value: status?.enabled_apis ?? 0, icon: Zap, color: 'text-orange-500' },
          { label: 'Suscritas', value: status?.verified_active ?? 0, icon: CheckCircle2, color: 'text-green-600' },
        ].map(({ label, value, icon: Icon, color }) => (
          <Card key={label}>
            <CardContent className="p-4 flex items-center gap-3">
              <Icon className={`h-8 w-8 ${color}`} />
              <div>
                <p className="text-xs text-muted-foreground">{label}</p>
                <p className="text-lg font-bold">{value}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* API Key section */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Key className="h-4 w-4 text-orange-500" />
            Clave maestra de RapidAPI
          </CardTitle>
          <CardDescription>
            Una sola clave da acceso a todas tus APIs suscritas en RapidAPI Hub.
            Consíguela en{' '}
            <a href="https://rapidapi.com/developer/dashboard" target="_blank" rel="noreferrer"
              className="text-indigo-600 underline hover:text-indigo-700">
              rapidapi.com → Dashboard
            </a>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 max-w-lg">
            <div className="relative flex-1">
              <Input
                type={showKey ? 'text' : 'password'}
                placeholder={status?.key_configured ? '••••••••••••••••••••••••••••' : 'Pega tu API key aquí'}
                value={apiKey}
                onChange={e => setApiKey(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && saveKey()}
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowKey(p => !p)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <Button onClick={saveKey} disabled={saving || !apiKey.trim()}
              className="bg-orange-500 hover:bg-orange-600 text-white">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              <span className="ml-2">Guardar</span>
            </Button>
          </div>
          {status?.key_configured && (
            <p className="mt-2 text-xs text-green-600 flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3" /> API key configurada. Pega una nueva para actualizarla.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Catalog */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1">
              <CardTitle className="text-base">Catálogo de APIs</CardTitle>
              <CardDescription className="mt-0.5">
                Activa/desactiva las APIs que usarán tus agentes IA
              </CardDescription>
            </div>
            <div className="flex gap-2 flex-wrap">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  className="pl-8 h-8 text-sm w-48"
                  placeholder="Buscar API..."
                  value={filter}
                  onChange={e => setFilter(e.target.value)}
                />
              </div>
              <select
                className="h-8 text-sm border rounded-md px-2 bg-background"
                value={categoryFilter}
                onChange={e => setCategoryFilter(e.target.value)}
              >
                {allCategories.map(c => (
                  <option key={c} value={c}>{c === 'all' ? 'Todas las categorías' : c}</option>
                ))}
              </select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {filteredApis.length === 0 ? (
            <div className="py-16 text-center text-muted-foreground">
              <Globe className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">No hay APIs que coincidan con el filtro</p>
            </div>
          ) : (
            <div className="divide-y">
              {filteredApis.map(api => (
                <div key={api.id} className={`p-4 transition-colors ${!api.enabled ? 'opacity-50' : ''}`}>
                  <div className="flex items-start gap-3">
                    {/* Icon */}
                    <div
                      className="h-10 w-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0"
                      style={{ background: `${api.color}22`, border: `1.5px solid ${api.color}55` }}
                    >
                      {api.icon}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-0.5">
                        <span className="font-semibold text-sm">{api.name}</span>
                        <SubscriptionBadge status={api.subscription_status} />
                        {api.is_custom && (
                          <span className="text-[10px] px-2 py-0.5 bg-indigo-50 text-indigo-600 border border-indigo-100 rounded-full font-medium">
                            Personalizada
                          </span>
                        )}
                        <span className="text-[10px] px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full">
                          {api.category}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground truncate">{api.description}</p>
                      <p className="text-[10px] text-slate-400 font-mono mt-0.5">{api.host}</p>

                      {/* Test area */}
                      {api.enabled && status?.key_configured && (
                        <div className="mt-2 flex gap-2 items-center">
                          <Input
                            className="h-7 text-xs w-44"
                            placeholder="Consulta de prueba..."
                            value={testQuery}
                            onChange={e => setTestQuery(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleTest(api.id)}
                          />
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs px-2"
                            onClick={() => handleTest(api.id)}
                            disabled={testingId === api.id}
                          >
                            {testingId === api.id
                              ? <Loader2 className="h-3 w-3 animate-spin" />
                              : <Play className="h-3 w-3" />
                            }
                            <span className="ml-1">Probar</span>
                          </Button>
                        </div>
                      )}

                      {/* Test results */}
                      {testResults[api.id] && (
                        <div className={`mt-2 rounded-lg p-2 text-xs font-mono max-h-32 overflow-auto ${
                          testResults[api.id].ok ? 'bg-slate-50 border border-slate-200' : 'bg-red-50 border border-red-200'
                        }`}>
                          {testResults[api.id].ok
                            ? `HTTP ${testResults[api.id].data.status} · ${JSON.stringify(testResults[api.id].data.raw).slice(0, 300)}...`
                            : `Error: ${testResults[api.id].error}`
                          }
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <div className="flex flex-col items-center gap-1">
                        <Switch
                          checked={api.enabled}
                          onCheckedChange={() => handleToggle(api.id, api.enabled)}
                        />
                        <span className="text-[9px] text-muted-foreground">
                          {api.enabled ? 'On' : 'Off'}
                        </span>
                      </div>
                      {api.is_custom && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-red-400 hover:text-red-600 hover:bg-red-50"
                          onClick={() => handleDelete(api.id, api.name)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Import Modal ── */}
      {showImport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Download className="h-4 w-4 text-orange-500" />
                Importar suscripciones
              </CardTitle>
              <CardDescription>
                Ve a{' '}
                <a href="https://rapidapi.com/developer/dashboard" target="_blank" rel="noreferrer"
                  className="text-indigo-600 underline">rapidapi.com → My Apps → API Subscriptions
                </a>
                , copia los hosts de tus APIs suscritas y pégalos aquí (uno por línea).
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-xs text-muted-foreground">Hosts (uno por línea)</Label>
                <textarea
                  className="w-full mt-1 rounded-md border bg-background p-2 text-sm font-mono min-h-[140px] focus:outline-none focus:ring-2 focus:ring-orange-400"
                  placeholder={'tasty.p.rapidapi.com\nshazam-core.p.rapidapi.com\nyoutube-v31.p.rapidapi.com'}
                  value={importHosts}
                  onChange={e => setImportHosts(e.target.value)}
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => { setShowImport(false); setImportHosts(''); }}>
                  Cancelar
                </Button>
                <Button onClick={handleImport} disabled={importing || !importHosts.trim()}
                  className="bg-orange-500 hover:bg-orange-600 text-white">
                  {importing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
                  Importar APIs
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ── Add API Modal ── */}
      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 overflow-y-auto">
          <Card className="w-full max-w-lg my-4">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Plus className="h-4 w-4 text-orange-500" />
                Añadir API personalizada
              </CardTitle>
              <CardDescription>Añade manualmente una API de RapidAPI al catálogo</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Nombre *</Label>
                  <Input className="mt-1" placeholder="Mi API" value={addForm.name}
                    onChange={e => setAddForm(p => ({ ...p, name: e.target.value }))} />
                </div>
                <div>
                  <Label className="text-xs">Host (RapidAPI) *</Label>
                  <Input className="mt-1 font-mono text-sm" placeholder="api.p.rapidapi.com" value={addForm.host}
                    onChange={e => setAddForm(p => ({ ...p, host: e.target.value }))} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Categoría</Label>
                  <Input className="mt-1" placeholder="general" value={addForm.category}
                    onChange={e => setAddForm(p => ({ ...p, category: e.target.value }))} />
                </div>
                <div>
                  <Label className="text-xs">Ícono (emoji)</Label>
                  <Input className="mt-1" placeholder="⚡" value={addForm.icon}
                    onChange={e => setAddForm(p => ({ ...p, icon: e.target.value }))} />
                </div>
              </div>
              <div>
                <Label className="text-xs">Descripción</Label>
                <Input className="mt-1" placeholder="¿Para qué sirve esta API?"
                  value={addForm.description}
                  onChange={e => setAddForm(p => ({ ...p, description: e.target.value }))} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">URL del endpoint (opcional)</Label>
                  <Input className="mt-1 text-sm font-mono" placeholder="https://api.p.rapidapi.com/search"
                    value={addForm.endpoint_url}
                    onChange={e => setAddForm(p => ({ ...p, endpoint_url: e.target.value }))} />
                </div>
                <div>
                  <Label className="text-xs">Parámetro de búsqueda</Label>
                  <Input className="mt-1" placeholder="query" value={addForm.query_param}
                    onChange={e => setAddForm(p => ({ ...p, query_param: e.target.value }))} />
                </div>
              </div>
              <div>
                <Label className="text-xs">Tags (separados por coma)</Label>
                <Input className="mt-1" placeholder="búsqueda, datos, IA" value={addForm.tags}
                  onChange={e => setAddForm(p => ({ ...p, tags: e.target.value }))} />
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <Button variant="outline" onClick={() => setShowAdd(false)}>Cancelar</Button>
                <Button onClick={handleAdd} disabled={adding || !addForm.name.trim() || !addForm.host.trim()}
                  className="bg-orange-500 hover:bg-orange-600 text-white">
                  {adding ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                  Añadir al catálogo
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
